import type { TFunction } from 'i18next';
import apiClient, { api } from './api';
import { fetchWithSession } from './fetchWithSession';
import { postWithSession } from './postWithSession';
import { Document, DocumentVersion, PaginatedResponse } from '../types';
import { translateWithFallback } from '../utils/translation';
import { apiErrorStatus } from '../utils/apiErrorMessages';
import { API_URL, getAccessToken, getHeaders } from './document.service.helpers';
import type {
  DuplicateCheckResult,
  UploadResponse,
  DocumentMetadata,
  DocumentContentResult,
  DocumentImagesResult,
  AnalyzeImageResult,
  AnalyzeAllImagesResult,
  ReExtractImagesResult,
  CompareImagesResult,
  AnalyzeImageSequenceResult,
  ReExtractMetadataResult,
} from './document.service.types';

export type { DuplicateCheckResult } from './document.service.types';

export interface DocumentCategoryOption {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
}

// The api client's 30 s default aborts large files on a slow link.
const DOWNLOAD_TIMEOUT_MS = 5 * 60 * 1000;

/** The RFC 8187 `filename*` the download route sends, or undefined when absent, malformed or blank. */
const dispositionFileName = (header: unknown): string | undefined => {
  const encoded = typeof header === 'string'
    ? /(?:^|;)\s*filename\*=UTF-8'[^']*'([^;\s]+)/i.exec(header)?.[1]
    : undefined;
  try {
    return (encoded && decodeURIComponent(encoded).trim()) || undefined;
  } catch {
    return undefined;
  }
};

const localize = (
  t: TFunction | undefined,
  key: string,
  fallback: string,
): string => (t ? translateWithFallback(t, key, fallback) : fallback);

/**
 * These endpoints are reached with `fetch`, so nothing normalizes them the way
 * api.ts does. The status is the contract the caller resolves against; the
 * response body is a log artifact and never enters `message`.
 */
const httpError = async (response: Response) => Object.assign(
  new Error(`HTTP ${response.status}`),
  {
    code: `HTTP_${response.status}`,
    details: await response.json().catch(() => ({})),
  },
);

class DocumentService {
  async getCategories(signal?: AbortSignal): Promise<DocumentCategoryOption[]> {
    const response = await fetchWithSession(`${API_URL}/documents/categories`, {
      method: 'GET',
      headers: getHeaders(),
      signal,
    });
    if (!response.ok) throw await httpError(response);
    return response.json();
  }

  // Upload document
  async uploadDocument(
    file: File,
    metadata: DocumentMetadata,
    onProgress?: (progress: number) => void,
    options?: { asNewVersion?: boolean },
    t?: TFunction
  ): Promise<UploadResponse> {
    try {
      // Create FormData for the upload endpoint
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', metadata.category || 'general');
      if (metadata.description) {
        formData.append('description', metadata.description);
      }
      if (metadata.tags && metadata.tags.length > 0) {
        formData.append('tags', metadata.tags.join(','));
      }
      // Add as_new_version flag if specified (for handling duplicate uploads)
      if (options?.asNewVersion) {
        formData.append('as_new_version', 'true');
      }

      // Upload to the documents endpoint (saves to database)
      // Note: Do NOT set Content-Type manually for FormData - axios will set it
      // automatically with the correct boundary parameter
      const token = getAccessToken();
      const response = await postWithSession<UploadResponse>(`${API_URL}/documents/upload`, formData, {
        headers: {
          // Let axios set Content-Type automatically for multipart/form-data
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        withCredentials: false,  // Cookie送信は無効化（Authorization header認証のみ）
        timeout: 120000,  // 2 minutes timeout for large file uploads
        onUploadProgress: (progressEvent: any) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return {
        id: response.data.id,
        fileName: response.data.fileName,
        fileSize: response.data.fileSize,
        mimeType: response.data.mimeType,
        status: response.data.status || 'completed',
        uploadedAt: response.data.uploadedAt,
        message: response.data.message || 'Upload successful',
      };
    } catch (error: any) {
      // Never log the axios error object itself: error.config.headers carries
      // the Authorization bearer token straight into the browser console.
      console.error('Upload failed:', error?.response?.status ?? error?.code ?? 'unknown');

      // Handle 409 Conflict (duplicate file) specially
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        // The 409 status is the contract; the server's Japanese prose is a log artifact.
        const duplicateMessage = (serverMessage?: string) => (
          t
            ? translateWithFallback(t, 'documents.duplicateUpload', '同じファイルが既にアップロードされています')
            : serverMessage || '同じファイルが既にアップロードされています'
        );

        // Handle both object and string formats
        if (detail && typeof detail === 'object') {
          // Object format: { message: string, existingDocument: {...} }
          const duplicateError: any = new Error(duplicateMessage(detail.message));
          duplicateError.isDuplicate = true;
          duplicateError.existingDocument = detail.existingDocument;
          throw duplicateError;
        } else if (typeof detail === 'string') {
          // String format: "同じファイルが既にアップロードされています"
          const duplicateError: any = new Error(duplicateMessage(detail));
          duplicateError.isDuplicate = true;
          duplicateError.existingDocument = null;
          throw duplicateError;
        }
      }

      // Carry the status as a code like every other rejection in this module,
      // so the caller's resolver can distinguish 401/403/413/429 rather than
      // collapsing every upload failure into one message.
      const status = error?.response?.status;
      throw Object.assign(
        new Error(localize(t, 'upload.uploadFailed', 'Upload failed')),
        { code: status ? `HTTP_${status}` : error?.code },
      );
    }
  }

  // Get documents list
  async getDocuments(params?: {
    page?: number;
    pageSize?: number;
    category?: string;
    tags?: string[];
    search?: string;
    fileType?: string;
    uploadedBy?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Document>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('page_size', params.pageSize.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.tags?.length) queryParams.append('tags', params.tags.join(','));
    if (params?.search?.trim()) queryParams.append('search', params.search.trim());
    if (params?.fileType) queryParams.append('file_type', params.fileType);
    if (params?.uploadedBy) queryParams.append('uploaded_by', params.uploadedBy);
    if (params?.dateFrom) queryParams.append('date_from', params.dateFrom);
    if (params?.dateTo) queryParams.append('date_to', params.dateTo);

    // Map frontend sort fields to backend field names
    if (params?.sortBy) {
      const sortFieldMap: { [key: string]: string } = {
        'date': 'uploadedAt',
        'name': 'fileName',
        'size': 'fileSize',
        'category': 'category',
      };
      const backendSortField = sortFieldMap[params.sortBy] || params.sortBy;
      queryParams.append('sort_by', backendSortField);
    }
    if (params?.sortOrder) queryParams.append('sort_order', params.sortOrder);

    const response = await fetchWithSession(`${API_URL}/documents/?${queryParams.toString()}`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = new Error('Failed to fetch documents') as Error & { code?: string };
      error.code = `HTTP_${response.status}`;
      throw error;
    }

    return await response.json();
  }

  /**
   * Asks whether a file name already exists. The page used to build this URL
   * itself and read the token straight out of sessionStorage, duplicating the
   * base-URL normalisation and auth this class already owns.
   */
  async checkDuplicate(fileName: string): Promise<DuplicateCheckResult> {
    const body = new FormData();
    body.append('fileName', fileName);

    try {
      const response = await fetchWithSession(`${API_URL}/documents/check-duplicate`, {
        method: 'POST',
        headers: getHeaders(false),
        body,
      });
      if (!response.ok) return { isDuplicate: false };
      return await response.json();
    } catch (error) {
      // A failed check must not block the upload; treat it as "not a duplicate".
      console.warn('Duplicate check unavailable', error);
      return { isDuplicate: false };
    }
  }

  // Get document by ID
  async getDocument(id: string): Promise<Document> {
    try {
      const response = await fetchWithSession(`${API_URL}/documents/${id}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        // Same shape as the api client's errors, so callers can tell a 404 from a network failure.
        throw Object.assign(new Error('Failed to fetch document'),
          { code: `HTTP_${response.status}`, details: { http_status: response.status } });
      }

      return await response.json();
    } catch (error) {
      if (apiErrorStatus(error) !== undefined) throw error;
      console.warn('Document detail request failed without an HTTP status');
      throw error;
    }
  }

  // Update document metadata
  async updateDocument(id: string, updates: Partial<DocumentMetadata>): Promise<Document> {
    return api.patch(`/documents/${id}`, updates);
  }

  // Update document tags
  async updateDocumentTags(id: string, tags: string[]): Promise<{ message: string; tags: string[] }> {
    return api.patch(`/documents/${id}/tags`, { tags });
  }

  // Delete document
  async deleteDocument(id: string): Promise<void> {
    return api.delete(`/documents/${id}`);
  }

  /**
   * Saves a document file; omitting `version` downloads the current one. The response's
   * `filename*` wins; `fallbackName` is used only when it is missing or unusable.
   * Rejects with the api client's normalized `ApiError`.
   */
  async downloadDocument(documentId: string, fallbackName: string, version?: number): Promise<void> {
    const response = await apiClient.get<Blob>(`/documents/${documentId}/download`, {
      responseType: 'blob',
      params: { version },
      timeout: DOWNLOAD_TIMEOUT_MS,
      // Timeouts reject as ETIMEDOUT, not ECONNABORTED; useDocumentDownload keys on it.
      transitional: { clarifyTimeoutError: true },
    });
    // A blob URL lives on this origin: keep a text/html or SVG file from ever rendering there.
    const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/octet-stream' }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = dispositionFileName(response.headers['content-disposition']) ?? fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoking in the same task as click() can cancel the download before it starts.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }

  // Get document versions
  async getDocumentVersions(documentId: string): Promise<DocumentVersion[]> {
    try {
      return await api.get(`/documents/${documentId}/versions`);
    } catch (error) {
      console.warn('Document versions API not available');
      return []; // Return empty array for now
    }
  }

  // Upload new version
  async uploadNewVersion(
    documentId: string,
    file: File,
    changeLog?: string,
    onProgress?: (progress: number) => void
  ): Promise<DocumentVersion> {
    const formData = new FormData();
    formData.append('file', file);
    if (changeLog) formData.append('changeLog', changeLog);

    // Note: Do NOT set Content-Type manually for FormData - axios will set it
    // automatically with the correct boundary parameter
    const token = getAccessToken();
    const response = await postWithSession<DocumentVersion>(`${API_URL}/documents/${documentId}/versions`, formData, {
      headers: {
        // Let axios set Content-Type automatically for multipart/form-data
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      withCredentials: false,  // Cookie送信は無効化（Authorization header認証のみ）
      timeout: 120000,  // 2 minutes timeout for large file uploads
      onUploadProgress: (progressEvent: any) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  // Get document extracted text content
  async getDocumentContent(documentId: string): Promise<DocumentContentResult> {
    try {
      const response = await fetchWithSession(`${API_URL}/documents/${documentId}/content`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw await httpError(response);
      }

      return await response.json();
    } catch (error) {
      console.error('Document content fetch failed:', error);
      throw error;
    }
  }

  // Get upload status
  async getUploadStatus(uploadId: string): Promise<UploadResponse> {
    try {
      // Try to get actual status from backend
      const response = await fetchWithSession(`${API_URL}/documents/${uploadId}/status`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (response.ok) {
        return await response.json();
      }

      // If endpoint doesn't exist (404), fall back to checking document exists
      if (response.status === 404) {
        // Try to get document info directly
        const docResponse = await fetchWithSession(`${API_URL}/documents/${uploadId}`, {
          method: 'GET',
          headers: getHeaders(),
        });

        if (docResponse.ok) {
          const doc = await docResponse.json();
          return {
            id: uploadId,
            fileName: doc.fileName || doc.title,
            fileSize: doc.fileSize || 0,
            mimeType: doc.mimeType || 'application/octet-stream',
            status: 'completed',
            uploadedAt: doc.uploadedAt || doc.createdAt || new Date().toISOString(),
            message: 'Processing completed',
          };
        }
      }

      throw new Error('Failed to get upload status');
    } catch (error) {
      console.warn('Upload status API not available, returning pending status');
      // Return pending status if we can't verify
      return {
        id: uploadId,
        fileName: 'Unknown',
        fileSize: 0,
        mimeType: 'application/octet-stream',
        status: 'processing',
        uploadedAt: new Date().toISOString(),
        message: 'Unable to retrieve status',
      };
    }
  }

  // Get document images
  async getDocumentImages(documentId: string): Promise<DocumentImagesResult> {
    try {
      const response = await fetchWithSession(`${API_URL}/documents/${documentId}/images`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch document images');
      }

      return await response.json();
    } catch (error) {
      console.warn('Document images API not available');
      return {
        documentId,
        totalImages: 0,
        images: [],
      };
    }
  }

  // Fetch document image with authentication and return blob URL
  async getDocumentImageBlob(documentId: string, imageId: string): Promise<string> {
    try {
      const response = await fetchWithSession(`${API_URL}/documents/${documentId}/images/${imageId}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Failed to fetch image blob:', error);
      throw error;
    }
  }

  // Analyze single image with AI
  async analyzeImage(documentId: string, imageId: string): Promise<AnalyzeImageResult> {
    try {
      const response = await fetchWithSession(`${API_URL}/documents/${documentId}/images/${imageId}/analyze`, {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze image');
      }

      return await response.json();
    } catch (error) {
      console.error('Image analysis failed:', error);
      throw error;
    }
  }

  // Analyze all images in document
  async analyzeAllImages(documentId: string, forceReanalyze: boolean = false): Promise<AnalyzeAllImagesResult> {
    try {
      const url = new URL(`${API_URL}/documents/${documentId}/images/analyze-all`);
      if (forceReanalyze) {
        url.searchParams.append('force_reanalyze', 'true');
      }

      const response = await fetchWithSession(url.toString(), {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze images');
      }

      return await response.json();
    } catch (error) {
      console.error('Batch image analysis failed:', error);
      throw error;
    }
  }

  // Re-extract images from document
  async reExtractImages(
    documentId: string,
    options: {
      asBlueprint?: boolean;
      dpi?: number;
      runAiAnalysis?: boolean;
    } = {}
  ): Promise<ReExtractImagesResult> {
    try {
      const url = new URL(`${API_URL}/documents/${documentId}/re-extract-images`);

      if (options.asBlueprint !== undefined) {
        url.searchParams.append('as_blueprint', String(options.asBlueprint));
      }
      if (options.dpi !== undefined) {
        url.searchParams.append('dpi', String(options.dpi));
      }
      if (options.runAiAnalysis !== undefined) {
        url.searchParams.append('run_ai_analysis', String(options.runAiAnalysis));
      }

      const response = await fetchWithSession(url.toString(), {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw await httpError(response);
      }

      return await response.json();
    } catch (error) {
      console.error('Image re-extraction failed:', error);
      throw error;
    }
  }

  // Compare two images
  async compareImages(
    documentId: string,
    image1Id: string,
    image2Id: string,
    comparisonType: 'changes' | 'evolution' | 'differences' = 'changes'
  ): Promise<CompareImagesResult> {
    try {
      const url = new URL(`${API_URL}/documents/${documentId}/images/compare`);
      url.searchParams.append('image1_id', image1Id);
      url.searchParams.append('image2_id', image2Id);
      url.searchParams.append('comparison_type', comparisonType);

      const response = await fetchWithSession(url.toString(), {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw await httpError(response);
      }

      return await response.json();
    } catch (error) {
      console.error('Image comparison failed:', error);
      throw error;
    }
  }

  // Analyze a sequence of images
  async analyzeImageSequence(
    documentId: string,
    imageIds: string[]
  ): Promise<AnalyzeImageSequenceResult> {
    try {
      const url = new URL(`${API_URL}/documents/${documentId}/images/analyze-sequence`);

      // Add each image ID as a separate query parameter
      imageIds.forEach(id => {
        url.searchParams.append('image_ids', id);
      });

      const response = await fetchWithSession(url.toString(), {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw await httpError(response);
      }

      return await response.json();
    } catch (error) {
      console.error('Image sequence analysis failed:', error);
      throw error;
    }
  }

  // Re-extract metadata (title, summary, tags) using LLM
  async reExtractMetadata(
    documentId: string,
    options: {
      extractTitle?: boolean;
      extractSummary?: boolean;
      extractTags?: boolean;
    } = {}
  ): Promise<ReExtractMetadataResult> {
    try {
      // Use FormData to match backend Form() parameters
      const formData = new FormData();
      formData.append('extract_title', String(options.extractTitle ?? true));
      formData.append('extract_summary', String(options.extractSummary ?? true));
      formData.append('extract_tags', String(options.extractTags ?? true));

      const token = getAccessToken();
      const response = await fetchWithSession(`${API_URL}/documents/${documentId}/re-extract-metadata`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        throw await httpError(response);
      }

      return await response.json();
    } catch (error) {
      console.error('Metadata re-extraction failed:', error);
      throw error;
    }
  }
}

export const documentService = new DocumentService();
export default documentService;
